/**
 * RTK Query endpoints for the email service's mailbox registry
 * (`v1/email/mailboxes`, services/email/src/mailboxes).
 *
 * Types mirror the authoritative DTO contract:
 *   services/email/src/mailboxes/dto/mailbox.dto.ts
 *
 * SECURITY BOUNDARY (binding): this surface is a vault-REFERENCE manager only.
 * The only representation of mail-account authentication material anywhere in
 * this file, its payloads, or the query cache is `secretsVaultRef` — an opaque
 * vault pointer string. No field that could hold secret material may ever be
 * added here (design spec §8.2; email-service DTOs enforce the same rule with
 * `.strict()` schemas).
 *
 * The email service does NOT wrap responses in `{ data }` (no
 * ResponseWrapperInterceptor in EmailModule — contrast with customers/planning),
 * so single reads use plain responses and the list keeps the CursorPage
 * envelope `{ data, pagination: { cursor, hasMore, totalCount? } }`.
 */
import { apiSlice } from './apiSlice';
import type { CursorPage } from './directoryApi';

/** Mailbox capability modes (mirror of the MailboxMode DB enum). */
export type MailboxMode = 'send' | 'receive' | 'both';
/** Receive protocols (mirror of the MailboxProtocol DB enum). */
export type MailboxProtocol = 'imap' | 'pop3';
/** Lifecycle statuses (mirror of the MailboxStatus DB enum). */
export type MailboxStatus = 'active' | 'disabled';

/** Per-mailbox receive behaviour (mirror of MailboxConfigSchema). */
export interface MailboxConfigDto {
  autoCreateTickets?: boolean;
  pollIntervalMs?: number;
}

/** POST /v1/email/mailboxes body — CreateMailboxSchema shape, `.strict()`: unknown keys 400. */
export interface CreateMailboxDto {
  address: string;
  mode: MailboxMode;
  /** Receive protocol — required for receive|both, forbidden for send. */
  protocol?: MailboxProtocol;
  /** Secrets-service vault reference (pointer ONLY — never secret material). */
  secretsVaultRef: string;
  /** Receive behaviour — forbidden for mode='send'. */
  config?: MailboxConfigDto;
}

/** PATCH /v1/email/mailboxes/:id body — UpdateMailboxSchema (all optional, `.strict()`; empty `{}` rejected). */
export type UpdateMailboxDto = Partial<CreateMailboxDto>;

/** HTTP projection of a Mailbox row (mirror of MailboxResponse). */
export interface MailboxResponse {
  id: string;
  orgId: string;
  address: string;
  mode: MailboxMode;
  /** Present ONLY for receive-capable rows (mode receive|both). */
  protocol?: MailboxProtocol;
  /** Secrets-service vault reference (pointer only). */
  secretsVaultRef: string;
  status: MailboxStatus;
  /** Receive behaviour — only present for receive-capable rows. */
  config?: MailboxConfigDto;
  createdAt: string;
  updatedAt: string;
}

/** GET query params — names exactly per MailboxListQuery + controller. */
export interface MailboxListParams {
  limit?: number;
  cursor?: string;
  status?: MailboxStatus;
  mode?: MailboxMode;
}

/**
 * Cursor envelope for the mailboxes list (design §2): the shared CursorPage
 * shape plus the optional `totalCount` the email endpoint supplies
 * (packages/common/src/dto/cursor-pagination.ts:67–82).
 */
export interface MailboxPage extends CursorPage<MailboxResponse> {
  pagination: { cursor: string | null; hasMore: boolean; totalCount?: number };
}

export const mailboxesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMailboxes: builder.query<MailboxPage, MailboxListParams | void>({
      query: (params) => ({ url: '/email/v1/email/mailboxes', params: params || undefined }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: 'Mailbox' as const, id })),
              { type: 'Mailbox', id: 'LIST' },
            ]
          : [{ type: 'Mailbox', id: 'LIST' }],
    }),
    getMailbox: builder.query<MailboxResponse, string>({
      query: (id) => `/email/v1/email/mailboxes/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Mailbox', id }],
    }),
    createMailbox: builder.mutation<MailboxResponse, CreateMailboxDto>({
      query: (body) => ({ url: '/email/v1/email/mailboxes', method: 'POST', body }),
      invalidatesTags: [{ type: 'Mailbox', id: 'LIST' }],
    }),
    updateMailbox: builder.mutation<MailboxResponse, { id: string; body: UpdateMailboxDto }>({
      query: ({ id, body }) => ({ url: `/email/v1/email/mailboxes/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Mailbox', id },
        { type: 'Mailbox', id: 'LIST' },
      ],
    }),
    /** Soft disable (status='disabled') — DELETE, no body; returns updated projection. */
    disableMailbox: builder.mutation<MailboxResponse, string>({
      query: (id) => ({ url: `/email/v1/email/mailboxes/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Mailbox', id },
        { type: 'Mailbox', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetMailboxesQuery,
  useGetMailboxQuery,
  useCreateMailboxMutation,
  useUpdateMailboxMutation,
  useDisableMailboxMutation,
} = mailboxesApi;
